import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { debounce } from "lodash";
import {
  suspendGAAdminUser,
  activateGAAdminUser,
  fetchActiveGAAdminUsers,
  fetchPendingGAAdminUsers,
  fetchSuspendedGAAdminUsers,
} from '@store/thunks/gaadminThunk';

const ManageUsersTableGAAdmin = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("active");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [showConfirm, setShowConfirm] = useState({ show: false, userId: null, action: null, userName: "" });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Debounced search handler
  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page on search
    }, 300),
    []
  );

  // Filter users based on search query (username only)
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    return users.filter(user =>
      `${user.salutation?.salutationText || ''} ${user.firstName} ${user.middleName || ''} ${user.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [users, searchQuery]);

  // Calculate pagination data
  const totalCount = filteredUsers.length;
  const pageCount = Math.ceil(totalCount / pagination.pageSize);
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredUsers
      .slice(start, end)
      .map((user, idx) => ({
        ...user,
        serialNo: start + idx + 1,
      }));
  }, [filteredUsers, pagination]);

  console.log("[GAAdmin] Paginated data:", paginatedData);
  console.log("[GAAdmin] Pagination state:", { pageIndex: pagination.pageIndex, pageCount, totalCount });

  useEffect(() => {
    handleActiveUsers();
    // eslint-disable-next-line
  }, []);

  const handleActiveUsers = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchActiveGAAdminUsers());
      // Handle both direct array and result.payload scenarios
      const usersArr = result?.payload || result || [];
      setUsers(Array.isArray(usersArr) ? usersArr : []);
    } catch (error) {
      console.error('Error fetching active users:', error);
      setUsers([]);
    }
    setIsLoading(false);
  };

  const handlePendingUsers = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchPendingGAAdminUsers());
      // Handle both direct array and result.payload scenarios
      const usersArr = result?.payload || result || [];
      setUsers(Array.isArray(usersArr) ? usersArr : []);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      setUsers([]);
    }
    setIsLoading(false);
  };

  const handleSuspendedUsers = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchSuspendedGAAdminUsers());
      // Handle both direct array and result.payload scenarios
      const usersArr = result?.payload || result || [];
      setUsers(Array.isArray(usersArr) ? usersArr : []);
    } catch (error) {
      console.error('Error fetching suspended users:', error);
      setUsers([]);
    }
    setIsLoading(false);
  };

  const handleSuspendUser = async (userId) => {
    setIsLoading(true);
    try {
      const result = await dispatch(suspendGAAdminUser(userId));

      if (result && result.success) {
        setMessage({ text: "User suspended successfully", type: "suspend" });

        // Immediately remove user from current view for better UX
        setUsers((prevUsers) => {
          console.log('Suspending user with ID:', userId, 'Type:', typeof userId);
          console.log('Current users:', prevUsers.map(u => ({ id: u.userId, type: typeof u.userId })));
          const newUsers = prevUsers.filter((user) => user.userId !== userId);
          console.log('Filtered users:', newUsers.map(u => ({ id: u.userId })));
          return newUsers;
        });

        // Delay reload to allow state update and re-render
        setTimeout(() => {
          window.location.reload();
        }, 100);

      } else {
        setMessage({ text: (result && result.message) || "Failed to suspend user", type: "error" });
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      setMessage({ text: "Failed to suspend user", type: "error" });
    }

    setIsLoading(false);
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    setShowConfirm({ show: false, userId: null, action: null, userName: "" });
  };

  const handleActivateUser = async (userId) => {
    setIsLoading(true);
    try {
      const result = await dispatch(activateGAAdminUser(userId));

      if (result && result.success) {
        setMessage({ text: "User activated successfully", type: "activate" });

        // Immediately remove user from current view for better UX
        setUsers((prevUsers) => prevUsers.filter((user) => user.userId !== userId));

        // Reload the page to ensure fresh data
        setTimeout(() => {
          window.location.reload();
        }, 100);

      } else {
        setMessage({ text: (result && result.message) || "Failed to activate user", type: "error" });
      }
    } catch (error) {
      console.error('Error activating user:', error);
      setMessage({ text: "Failed to activate user", type: "error" });
    }

    setIsLoading(false);
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    setShowConfirm({ show: false, userId: null, action: null, userName: "" });
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("serialNo", {
      header: () => <span>Sr. No.</span>,
      cell: ({ row }) => <span className="text-gray-700">{row.original.serialNo}</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("name", {
    header: () => <span>Name</span>,
    cell: ({ row }) => <span className="text-gray-700 font-medium">{row.original.name}</span>,
    enableSorting: true,
  }),
  columnHelper.accessor("roleName", {
    header: () => <span>Role</span>,
    cell: ({ row }) => <span className="text-gray-700">{row.original.roleName || ""}</span>,
    enableSorting: true,
  }),
    columnHelper.accessor("gender", {
      header: () => <span>Gender</span>,
      cell: ({ row }) => <span className="text-gray-700">{row.original.gender || ""}</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("action", {
      header: () => <span>Action</span>,
      cell: ({ row }) => {
        const userId = row.original.userId;
        const { name } = row.original;

        return (
          <div className="flex justify-center space-x-2">
            {activeTab === "active" && (
              <button
                className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-150 text-sm font-medium"
                onClick={() => setShowConfirm({ show: true, userId, action: "suspend",  userName: name })}
              >
                Suspend
              </button>
            )}
            {activeTab === "pending" && (
              <>
                <button
                  className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-150 text-sm font-medium"
                  onClick={() => setShowConfirm({ show: true, userId, action: "activate", userName:name })}
                >
                  Activate
                </button>
                <button
                  className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-150 text-sm font-medium"
                  onClick={() => setShowConfirm({ show: true, userId, action: "suspend", userName:name })}
                >
                  Suspend
                </button>
              </>
            )}
            {activeTab === "suspended" && (
              <button
                className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-150 text-sm font-medium"
                onClick={() => setShowConfirm({ show: true, userId, action: "activate", userName:name })}
              >
                Activate
              </button>
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: pageCount,
    state: {
      pagination: { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize },
    },
    onPaginationChange: setPagination,
  });

  const renderSkeletonLoader = () => (
    <div className="space-y-4">
      <div className="h-10 bg-gray-200 rounded w-full"></div>
      {[...Array(5)].map((_, index) => (
        <div key={index} className="h-12 bg-gray-100 rounded w-full"></div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        {renderSkeletonLoader()}
      </div>
    );
  }

  if (!paginatedData || paginatedData.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <div className="flex space-x-2 mb-4 sm:mb-0">
            {["active", "pending", "suspended"].map(tab => (
              <button
                key={tab}
                className={`px-4 py-2 rounded-md text-sm font-medium transition duration-150 ${activeTab === tab
                  ? 'bg-[#ec7026] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === "active") handleActiveUsers();
                  else if (tab === "pending") handlePendingUsers();
                  else handleSuspendedUsers();
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)} Users
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search users by name..."
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] w-full sm:w-64 text-base font-['Semantic/Strong'] text-gray-700"
            onChange={(e) => debouncedSetSearchQuery(e.target.value)}
            defaultValue={searchQuery}
          />
        </div>
        <div className="bg-white shadow-lg rounded-2xl p-8 text-center w-full max-w-4xl mx-auto border border-gray-200">
          <svg
            className="w-20 h-20 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17 20h5v-2a2 2 0 00-2-2h-3m-2-4h7m-7 4h7m-7-8h7M3 20h5v-2a2 2 0 00-2-2H3m0-4h7m-7 4h7m-7-8h7"
            />
          </svg>
          <h2 className="text-3xl font-semibold text-gray-800 mb-4">
            No {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Users Found
          </h2>
          <p className="text-gray-600 text-lg mb-6">
            No {activeTab} users are available. Try checking another tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg transition-all duration-300 ${message.type === "activate"
            ? "bg-green-100 text-green-700"
            : message.type === "suspend"
              ? "bg-red-100 text-red-700"
              : "bg-red-100 text-red-700"
            }`}
        >
          {message.text}
        </div>
      )}

      {showConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full transform transition-all duration-200 scale-100">
            <div className="flex items-center justify-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${showConfirm.action === "activate"
                ? "bg-green-100"
                : "bg-red-100"
                }`}>
                {showConfirm.action === "activate" ? (
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center font-['Semantic/Strong']">
              Confirm {showConfirm.action === "activate" ? "Activation" : "Suspension"}
            </h3>
            <div className="text-center mb-8">
              <p className="text-gray-600 mb-3 text-lg font-['Semantic/Strong']">
              {console.log(showConfirm)}
                Are you sure you want to {showConfirm.action} this user?
              </p>
              {showConfirm.userName && (
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-[#ec7026]">
                  <p className="text-gray-800 font-semibold font-['Semantic/Strong']">
                    User: {showConfirm.userName}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 font-medium font-['Semantic/Strong'] min-w-[120px]"
                onClick={() => setShowConfirm({ show: false, userId: null, action: null, userName: "" })}
              >
                Cancel
              </button>
              <button
                className={`px-6 py-3 ${showConfirm.action === "activate"
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-red-500 hover:bg-red-600"
                  } text-white rounded-lg transition duration-200 font-medium font-['Semantic/Strong'] min-w-[120px] shadow-lg`}
                onClick={() => {
                  if (showConfirm.action === "activate") {
                    handleActivateUser(showConfirm.userId);
                  } else {
                    handleSuspendUser(showConfirm.userId);
                  }
                }}
              >
                {showConfirm.action === "activate" ? "Activate User" : "Suspend User"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <div className="flex space-x-2 mb-4 sm:mb-0">
          {["active", "pending", "suspended"].map(tab => (
            <button
              key={tab}
              className={`px-4 py-2 rounded-md text-sm font-medium transition duration-150 ${activeTab === tab
                ? 'bg-[#ec7026] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              onClick={() => {
                setActiveTab(tab);
                if (tab === "active") handleActiveUsers();
                else if (tab === "pending") handlePendingUsers();
                else handleSuspendedUsers();
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} Users
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search users by name..."
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] w-full sm:w-64 text-base font-['Semantic/Strong'] text-gray-700"
          onChange={(e) => debouncedSetSearchQuery(e.target.value)}
          defaultValue={searchQuery}
        />
      </div>

      <div className="bg-white shadow-lg rounded-2xl overflow-hidden">
        <table className="min-w-full border-collapse border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="bg-white border border-gray-200 px-4 py-2"
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      cursor: header.column.getCanSort() ? "pointer" : "default",
                    }}
                  >
                    <div className="text-center font-bold text-gray-700 text-base">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() && (
                        <span className="ml-1 text-gray-400">
                          {{
                            asc: "↑",
                            desc: "↓",
                          }[header.column.getIsSorted()] || ""}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-gray-100 transition-colors duration-200"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="border border-gray-200 px-4 py-2 text-center text-base font-['Semantic/Strong'] text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-6 py-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPagination((prev) => ({ ...prev, pageIndex: 0 }))}
            disabled={pagination.pageIndex === 0}
            className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
            title="First Page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7m0 0l7-7m-7 7h18M4 12h14" />
            </svg>
          </button>
          <button
            onClick={() => setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))}
            disabled={pagination.pageIndex === 0}
            className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
            title="Previous Page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-base font-['Semantic/Strong'] text-gray-700">
            Page {pagination.pageIndex + 1} of {pageCount}
          </span>
          <button
            onClick={() => setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))}
            disabled={pagination.pageIndex >= pageCount - 1}
            className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
            title="Next Page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={() => setPagination((prev) => ({ ...prev, pageIndex: pageCount - 1 }))}
            disabled={pagination.pageIndex >= pageCount - 1}
            className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
            title="Last Page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <select
          value={pagination.pageSize}
          onChange={(e) => setPagination((prev) => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }))}
          className="px-2 py-2 border border-gray-300 rounded-lg text-base font-['Semantic/Strong'] text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-150"
        >
          {[10, 20, 30, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize} entries
            </option>
          ))}
        </select>
      </div>
      {totalCount > 0 && (
        <div className="px-6 py-2 text-sm text-gray-600 font-['Semantic/Regular']">
          Showing {pagination.pageIndex * pagination.pageSize + 1} to{" "}
          {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of {totalCount} entries
        </div>
      )}
    </div>
  );
};

export default ManageUsersTableGAAdmin;